import React, { useState, useEffect } from 'react';
import {
  func,
  arrayOf,
  shape,
} from 'prop-types';
import {
  RewardSelection,
  RewardSelectionError,
  RewardSelectionPopup,
} from '@hyphengroup/cag-components';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import withSubscribe from 'containers/withSubscribe';
import SupportButtonStateContainer from 'containers/SupportButtonStateContainer';
import styled from 'styled-components';
import { path, reject, isNil } from 'ramda';
import { withTracking } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { getObjectFromArrayByKey } from 'helpers/Utils';

const Container = styled.div`
  min-height: calc(100vh - 160px);

  @media screen and (min-width: 768px) {
    min-height: calc(100vh - 160px);
    max-width: 490px;
    margin: 0 auto;
  }

  @media screen and (min-width: 960px) {
    min-height: calc(100vh - 205px);
  }
`;

const RewardPageContent = (props) => {
  const {
    data,
    error,
    redeemSubmissionWithoutPopup,
    staticTextList,
    imageAndLinkList,
    subscribedContainers,
    track,
  } = props;

  const [supportButtonState] = subscribedContainers;
  const { isLoaded } = supportButtonState.state;
  const [showPopup, setShowPopup] = useState(null);
  const [displayError, setDisplayError] = useState(false);
  const [redeemRewardKey, setRedeemRewardKey] = useState(null);
  const [rewardSelectionData, setRewardSelectionData] = useState([]);

  const toggleSupportBtn = (flag) => {
    if (!isLoaded) return;
    if (window.zE) {
      if (!flag) {
        window.zE('webWidget', 'hide');
      } else {
        window.zE('webWidget', 'show');
      }
    }
  };

  useEffect(() => {
    toggleSupportBtn(displayError);
  }, [displayError, isLoaded]);

  useEffect(() => {
    let tmpFieldsData = {};
    const haveRewardList = data?.redeemRewardPage?.fields?.some(item => {
      if (item.fieldId?.key?.toUpperCase() === 'REWARD') {
        tmpFieldsData = getObjectFromArrayByKey(path(['options'], item), 'key');
        setRedeemRewardKey(item.fieldId.key);
        return true;
      }
      return false;
    });

    const tmpRewardData = reject(isNil, data?.redeemRewardPage?.reward?.map(item => {
      if (item?.title && tmpFieldsData[item.title]) {
        let bottomLabelVal = null;
        let bottomTextVal = null;
        if (!item?.available) {
          bottomLabelVal = item?.expectedReplenishment
            ? staticTextList?.REWARD_LIST_BOTTOM_LABEL_REPLENISHING_DATE
            : staticTextList?.REWARD_LIST_BOTTOM_LABEL_REPLENISHING;
          bottomTextVal = item?.expectedReplenishment
            ? item.expectedReplenishment
            : "";
        } else if (item?.minExpiry) {
          bottomLabelVal = staticTextList?.REWARD_LIST_BOTTOM_LABEL;
          bottomTextVal = item.minExpiry;
        }
        return {
          title: item?.header,
          subTitle: item?.description?.shortDescription,
          bottomLabel: bottomLabelVal,
          bottomText: bottomTextVal,
          buttonIcon: item?.available ? null : "clock",
          buttonText: item?.available
            ? staticTextList?.REWARD_LIST_BUTTON_LABEL
            : staticTextList?.REWARD_LIST_BUTTON_LABEL_REPLENISHING,
          buttonEnabled: item?.available,
          key: item?.title,
        };
      }
      return null;
    })) || [];

    setRewardSelectionData(tmpRewardData);

    const IS_DISPLAY_ERROR = !!(error
      || !haveRewardList
      || !tmpRewardData
      || tmpRewardData.length === 0
    );

    setDisplayError(IS_DISPLAY_ERROR);

    if (!IS_DISPLAY_ERROR) {
      setShowPopup(true);
    }
  }, []);

  const closeModel = () => {
    track({
      data: {
        event: 'login_dashboard_rewards_got_it_abandon',
        location: 'login - rewards selection page',
        intent: 'abandon reward claim',
        dashboardModule: 'rewards',
      },
    });
    setShowPopup(false);
  };

  const closeModelByConfirm = () => {
    track({
      data: {
        event: 'login_dashboard_rewards_got_it',
        location: 'login - rewards selection page',
        intent: 'begin reward claim',
        dashboardModule: 'rewards',
      },
    });
    setShowPopup(false);
  };

  const rewardSelectionPopupData = [{
    name: 'REWARD STEP1',
    img: {
      alt: imageAndLinkList?.REWARD_STEP1?.image?.title,
      src: imageAndLinkList?.REWARD_STEP1?.image?.url,
    },
    text: staticTextList?.REWARD_LIST_POPUP_STEP1_LABEL,
  },
  {
    name: 'REWARD STEP2',
    img: {
      alt: imageAndLinkList?.REWARD_STEP2?.image?.title,
      src: imageAndLinkList?.REWARD_STEP2?.image?.url,
    },
    text: staticTextList?.REWARD_LIST_POPUP_STEP2_LABEL,
  },
  {
    name: 'REWARD STEP3',
    img: {
      alt: imageAndLinkList?.REWARD_STEP3?.image?.title,
      src: imageAndLinkList?.REWARD_STEP3?.image?.url,
    },
    text: staticTextList?.REWARD_LIST_POPUP_STEP3_LABEL,
  }];

  return (
    <CustomerProfileContext.Consumer>
      {({ loginState }) => (
        (displayError
          && (
            <Container>
              <RewardSelectionError
                heroImage={imageAndLinkList?.REWARD_SELECTION_ERROR?.image}
                staticText={staticTextList}
              />
            </Container>
          )
        )
        || (!displayError
          && (
            <Container>
              <RewardSelection
                staticText={staticTextList}
                rewardList={rewardSelectionData}
                handlers={{
                  onSelectReward: (item) => {
                    if (loginState === 'LOGGED_IN') {
                      redeemSubmissionWithoutPopup(
                        item,
                        redeemRewardKey,
                        data?.redeemRewardPage?.token
                      );
                    } else {
                      const redirectUri = encodeURIComponent(window.location.href);
                      window.location.href = `/login?redirect_uri=${redirectUri}`;
                    }
                  },
                }}
              />
              <RewardSelectionPopup
                staticText={staticTextList}
                isPopupShown={showPopup}
                list={rewardSelectionPopupData}
                handlers={{
                  onClose: closeModel,
                  onConfirm: closeModelByConfirm,
                }}
              />
            </Container>
          )
        )
      )}
    </CustomerProfileContext.Consumer>
  );
};

RewardPageContent.propTypes = {
  data: shape({}),
  error: shape({}),
  staticTextList: shape({}),
  imageAndLinkList: shape({}),
  selectReward: func,
  redeemSubmissionWithoutPopup: func,
  track: func.isRequired,
  subscribedContainers: arrayOf(shape({})),
};
RewardPageContent.defaultProps = {
  data: {},
  error: null,
  staticTextList: {},
  imageAndLinkList: {},
  selectReward: null,
  redeemSubmissionWithoutPopup: null,
  subscribedContainers: [],
};

export default withSubscribe(withTracking(withApplicationConfig(RewardPageContent, ['languages'])))([SupportButtonStateContainer]);
